import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { CustomerNote } from '../services/customerService';
import { Button, Avatar } from '../../../components';
import { formatDate } from '../../../lib/utils';

interface CustomerNotesProps {
  notes: CustomerNote[];
  onAddNote: (content: string) => void;
}

export default function CustomerNotes({ notes, onAddNote }: CustomerNotesProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const handleSubmit = () => {
    if (!noteContent.trim()) return;
    onAddNote(noteContent);
    setNoteContent('');
    setIsAdding(false);
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-ink">Notes</h4>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="rounded-xl p-1 text-ink-2 hover:bg-black/[0.04]"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {isAdding && (
        <div className="mb-3 space-y-2">
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Add a note..."
            rows={3}
            className="block w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSubmit}>
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setNoteContent('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-sm text-ink-3">No notes yet</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="rounded-card border border-line bg-surface-2 p-3">
              <div className="flex items-start gap-2">
                <Avatar name={note.staffName} size="xs" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-ink">
                      {note.staffName}
                    </p>
                    <span className="text-xs text-ink-3">
                      {formatDate(note.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-ink-2">{note.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
